import {CheckmarkIcon} from '@sanity/icons/Checkmark'
import {RemoveIcon} from '@sanity/icons/Remove'
import {clsx} from 'clsx/lite'
import {useEffect, useImperativeHandle, useRef} from 'react'
import {styled} from 'styled-components'

import {useCustomValidity} from '../../hooks/useCustomValidity'
import {inputElementStyles} from './styles'

import {checkbox} from './checkbox.css'

/**
 * @public
 */
export interface CheckboxProps {
  indeterminate?: boolean
  customValidity?: string
}

const Input = styled.input(inputElementStyles)

/**
 * Checkboxes allow the user to select one or more items from a set.
 *
 * @public
 */
export function Checkbox(
  props: Omit<React.HTMLProps<HTMLInputElement>, 'as' | 'type'> & CheckboxProps,
) {
  const {
    checked,
    className,
    disabled,
    indeterminate,
    customValidity,
    readOnly,
    ref: forwardedRef,
    style,
    ...restProps
  } = props
  const ref = useRef<HTMLInputElement | null>(null)

  useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(
    forwardedRef,
    () => ref.current,
  )

  useEffect(() => {
    if (ref.current) {
      // Set the indeterminate state
      ref.current.indeterminate = indeterminate || false
    }
  }, [indeterminate])

  useCustomValidity(ref, customValidity)

  return (
    <div className={clsx(checkbox, className)} data-ui="Checkbox" style={style}>
      <Input
        data-read-only={!disabled && readOnly ? '' : undefined}
        data-error={customValidity ? '' : undefined}
        data-ui="Checkbox__input"
        {...restProps}
        checked={checked}
        disabled={disabled || readOnly}
        type="checkbox"
        readOnly={readOnly}
        ref={ref}
      />
      <span>
        <CheckmarkIcon />
        <RemoveIcon />
      </span>
    </div>
  )
}
